import { Component, Injector, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import {
  FcClaimClearingHouseDataDto,
  FcClaimElectronicPayerDataDto,
  FcClaimsListItemDto,
  FindControlAdditionalSearchParametersDto,
  FindControlAdditionalSearchParametersType,
  FindControlRequiredSearchParametersDto,
  FormGroupValidationRules,
  SystemGuid,
} from 'webcommon/shared';

import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { FcItemListBaseComponent, FcListValidationService, FormControlExtended } from '../../generic';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'ap-fc-claims-list',
  templateUrl: './fc-claims-list.component.html',
  styleUrls: ['./fc-claims-list.component.less'],
})
export class FcClaimsListComponent extends FcItemListBaseComponent implements OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: FcClaimsListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;

  public isClaimElectronicPayerFindControlRequired = false;
  public isElectronicClaimFormatFindControlRequired = false;
  public showClaimStatusPayerControl = false;

  public selectedClaimElectronicPayerClearinghouseUid: string | null = null;

  public clearingHousePks: string[] = [];
  public electronicPayerPks: string[] = [];
  public electronicClaimFormatPks: string[] = [];
  public paperClaimFormatPks: string[] = [];
  public paperClaimDefaultOptionPks: string[] = [];
  public scrubProfilePks: string[] = [];
  public claimStatusPayerPks: string[] = [];

  public clearingHouseUidControl = new FormControl<string[]>([]);
  public electronicPayerUidControl = new FormControlExtended<string[]>([]);
  public electronicClaimFormatUidControl = new FormControlExtended<string[]>([]);
  public paperClaimFormatUidControl = new FormControl<string[]>([]);
  public paperClaimDefaultOptionUidControl = new FormControl<string[]>([]);
  public scrubProfileUidControl = new FormControl<string[]>([]);
  public acceptAssignmentStatusControl = new FormControl<string>('Accept Assignment');
  public memberIdFormatControl = new FormControl<string>('');
  public groupIdFormatControl = new FormControl<string>('');
  public claimStatusPayerUidControl = new FormControl<string[]>([]);

  public requiredSearchParameters = {
    Type: FindControlRequiredSearchParametersDto,
    RequiredSearchParameters: [
      {
        ColumnName: 'ListType',
        Negate: false,
        Value: 'PaperClaimOption',
      },
    ],
  };

  public claimElectronicPayerAdditonalSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [],
  };

  public readonly listItemDefaultValidation: FormGroupValidationRules = {
    ScrubProfileUid: {
      IsRequired: true,
    },
    MemberIDFormat: {
      MaxLength: 50,
      RegExp: '[aA9\\-?\\s]{0,50}',
    },
    GroupIDFormat: {
      MaxLength: 50,
      RegExp: '[aA9\\-?\\s]{0,50}',
    },
  };

  constructor(
    injector: Injector,
  ) {
    super(injector);

    this.clearingHouseUidControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe((clearingHouseUids: string[]) => {
      // Show hidden Claim Electronic Payer FindControl
      if (clearingHouseUids.length > 0) {
        const specificClearingHouseUids = [SystemGuid.eEMEDIXClearinghouseItemUid, SystemGuid.eEMEDIXFQHCClearinghouseItemUid];
        this.showClaimStatusPayerControl = specificClearingHouseUids.includes(clearingHouseUids[0]);
        if (!this.showClaimStatusPayerControl) {
          this.claimStatusPayerPks = [];
        }
      } else {
        this.showClaimStatusPayerControl = false;
        this.claimStatusPayerPks = [];
      }

      // Make Claim Electronic Payer FindControl required when ClearingHouse FindControl is not empty
      this.isClaimElectronicPayerFindControlRequired = clearingHouseUids.length > 0;
      this.electronicPayerUidControl.showErrorFlag = true;
    });

    // Make Electronic Claim Format FindControl required when Claim Electronic Payer FindControl is not empty
    this.electronicPayerUidControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe((electronicPayerUids: string[]) => {
      this.isElectronicClaimFormatFindControlRequired = electronicPayerUids.length > 0;
      this.electronicClaimFormatUidControl.showErrorFlag = true;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    }
    if (changes.form != null) {
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public addControlsToForm(): void {
    this.form.addControl('ClearinghouseUid', this.clearingHouseUidControl);
    this.form.addControl('ElectronicPayerUid', this.electronicPayerUidControl);
    this.form.addControl('ElectronicClaimFormatUid', this.electronicClaimFormatUidControl);
    this.form.addControl('PaperClaimFormatUid', this.paperClaimFormatUidControl);
    this.form.addControl('PaperClaimDefaultOptionUid', this.paperClaimDefaultOptionUidControl);
    this.form.addControl('ScrubProfileUid', this.scrubProfileUidControl);
    this.form.addControl('AcceptAssignmentStatus', this.acceptAssignmentStatusControl);
    this.form.addControl('MemberIDFormat', this.memberIdFormatControl);
    this.form.addControl('GroupIDFormat', this.groupIdFormatControl);
    this.form.addControl('ClaimStatusPayerUid', this.claimStatusPayerUidControl);
  }

  public getFindControlValidation(controlName: string): any {
    if (this.fcListValidation.getValidators(controlName).length > 0) {
      return this.fcListValidation.getValidators(controlName);
    } else {
      return this.listItemDefaultValidation[controlName]?.IsRequired;
    }
  }

  public onClearinghouseDataPopulate(clearinghouseArray: FcClaimClearingHouseDataDto[]): void {
    const clearinghouseData: FcClaimClearingHouseDataDto = { ...clearinghouseArray[0] };
    const AdditionalSearchParameters = [];
    if (clearinghouseData.Name) {
      AdditionalSearchParameters.push({
        ColumnName: 'ClaimClearingHouseUid',
        Value: clearinghouseData.ClaimClearingHouseUid,
      });
    }
    if (clearinghouseData.ClaimClearingHouseUid !== this.selectedClaimElectronicPayerClearinghouseUid) {
      this.electronicPayerPks = [];
    }
    this.claimElectronicPayerAdditonalSearchParameters = {
      Type: FindControlAdditionalSearchParametersType.Standard,
      AdditionalSearchParameters,
    };
  }

  public onClaimElectronicPayerDataPopulate(claimElectronicPayerArray: FcClaimElectronicPayerDataDto[]): void {
    const claimElectronicPayerData: FcClaimElectronicPayerDataDto = { ...claimElectronicPayerArray[0] };
    this.selectedClaimElectronicPayerClearinghouseUid = claimElectronicPayerData?.ClaimClearingHouseUid ?? null;
  }

  private setForm(): void {
    this.clearingHousePks = this.getFindControlValue(this.listItem?.ClearinghouseUid ?? '');
    this.electronicPayerPks = this.getFindControlValue(this.listItem?.ElectronicPayerUid ?? '');
    this.electronicClaimFormatPks = this.getFindControlValue(this.listItem?.ElectronicClaimFormatUid ?? '');
    this.paperClaimFormatPks = this.getFindControlValue(this.listItem?.PaperClaimFormatUid ?? '');
    this.scrubProfilePks = this.getFindControlValue(this.listItem?.ScrubProfileUid ?? '');
    this.claimStatusPayerPks = this.getFindControlValue(this.listItem?.ClaimStatusPayerUid ?? '');

    if (this.listItem?.AcceptAssignment) {
      this.acceptAssignmentStatusControl.setValue('Accept Assignment');
    } else if (this.listItem?.AcceptAssignmentLabOnly) {
      this.acceptAssignmentStatusControl.setValue('Accept Assignment, Lab Only');
    } else {
      this.acceptAssignmentStatusControl.setValue('Do not Accept Assignment');
    }

    // Member ID Control
    this.memberIdFormatControl.setValidators(this.fcListValidation.getValidators('MemberIDFormat'));
    this.memberIdFormatControl.setValue(this.listItem?.MemberIDFormat ?? '');

    // Group ID Control
    this.groupIdFormatControl.setValidators(this.fcListValidation.getValidators('GroupIDFormat'));
    this.groupIdFormatControl.setValue(this.listItem?.GroupIDFormat ?? '');

    if (this.itemContext.mode === FcItemModalMode.New) {
      this.paperClaimDefaultOptionPks = this.getFindControlValue(
        this.listItem?.PaperClaimDefaultOptionUid ?? SystemGuid.PaperClaimDefaultOptionUid
      );
    } else {
      this.paperClaimDefaultOptionPks = this.getFindControlValue(this.listItem?.PaperClaimDefaultOptionUid ?? '');
    }
  }
}
